import { ActivityIndicator, Pressable, Text, View } from "react-native";
import {
  formatDayBadge,
  formatDurationLabel,
  formatShortDayLabel,
  monthLabel,
  type Session,
  type WorkoutMonthSection,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { Icon } from "../Icon/Icon";
import { useHover } from "../useHover";
import type { WorkoutsStyles } from "./workoutsStyles";

type Tr = (k: string, opts?: Record<string, unknown>) => string;

// Presentational row + month-header for WorkoutsView's SectionList. All state
// + handlers arrive via props; these only render. Shared by the mobile screen
// (tap → edit in the logger, ↻ repeat, long-press delete) and the web-rnw
// console (tap → select for the detail pane; no repeat / delete affordances).

export function WorkoutListRow({
  item,
  selected,
  selectable = true,
  opening,
  repeating,
  deleting,
  onSelect,
  onRepeat,
  onDelete,
  styles,
  theme,
  t,
  locale,
}: {
  item: Session;
  selected: boolean;
  /**
   * Whether tapping the row does anything. Mobile = true (→ edit in logger);
   * the web console passes false until the master-detail detail pane lands
   * (S5) so the row is never a button that does nothing.
   */
  selectable?: boolean;
  opening: boolean;
  repeating: boolean;
  deleting: boolean;
  onSelect: (id: string) => void;
  /** Repeat affordance (mobile only); omit on web → no ↻ column. */
  onRepeat?: (id: string) => void;
  /** Long-press delete (mobile only); omit on web → no destructive gesture. */
  onDelete?: (item: Session) => void;
  styles: WorkoutsStyles;
  theme: UiTheme;
  t: Tr;
  locale: string;
}) {
  // Web-only: hover reveals the full name in a bubble (uniform single-line
  // rows can't show a long name inline). onHoverIn never fires on touch, so
  // `hovered` stays false on mobile and the bubble never renders there.
  const { hovered: hoverKey, hoverProps } = useHover();
  const hovered = hoverKey !== null;
  const badge = formatDayBadge(item.startedAt, locale);
  const duration = formatDurationLabel(item.startedAt, item.endedAt);
  const name = item.name ?? t("mobile.workouts.fallbackName");
  const imported = item.source === "healthconnect";
  const importedLabel = t("mobile.workouts.importedBadge", {
    defaultValue: "Imported",
  });
  // Screen readers announce a bare Pressable as "button" only; compose the
  // workout name + day badge so each row is distinguishable in a long list.
  // Append provenance so the "Imported" chip isn't a sighted-only cue.
  // An auto name already ends with its short date: don't say the day twice.
  const short = formatShortDayLabel(item.startedAt, locale);
  const named = short && name.endsWith(short)
    ? name
    : `${name}, ${badge.day} ${badge.dow}`;
  const rowLabel = imported ? `${named}, ${importedLabel}` : named;
  const body = (
    <>
      <View style={styles.rowDateBlock}>
        <Text style={styles.rowDateDay}>{badge.day}</Text>
        <Text style={styles.rowDateDow}>{badge.dow}</Text>
      </View>
      <View style={styles.rowMain}>
        <Text
          style={[styles.rowName, selected && styles.rowNameSelected]}
          numberOfLines={short && name.endsWith(short) ? 3 : 2}
        >
          {name}
        </Text>
        {duration || imported ? (
          <View style={styles.rowSecondaryRow}>
            {duration ? (
              <Text style={styles.rowSecondary} numberOfLines={1}>
                {duration}
              </Text>
            ) : null}
            {imported ? (
              <View style={styles.hcBadge}>
                <Text style={styles.hcBadgeText} accessible={false}>
                  {importedLabel}
                </Text>
              </View>
            ) : null}
          </View>
        ) : null}
        {hovered ? (
          <View style={styles.nameTip} pointerEvents="none">
            <Text style={styles.nameTipText}>{name}</Text>
          </View>
        ) : null}
      </View>
      {opening || deleting ? (
        <ActivityIndicator size="small" color={theme.palette.ink3} />
      ) : null}
    </>
  );
  const interactive = selectable || !!onDelete;
  return (
    <View style={[styles.rowWrap, hovered && styles.rowWrapHover]}>
      {interactive ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={rowLabel}
          accessibilityState={{ selected }}
          accessibilityHint={onDelete ? t("workout.deleteConfirm") : undefined}
          style={({ pressed }) => [
            styles.row,
            selected && styles.rowSelected,
            hovered && !selected && styles.rowHover,
            pressed && styles.rowPressed,
          ]}
          onPress={selectable ? () => onSelect(item.id) : undefined}
          onLongPress={onDelete ? () => onDelete(item) : undefined}
          delayLongPress={500}
          {...hoverProps(true)}
        >
          {body}
        </Pressable>
      ) : (
        <View
          accessibilityLabel={rowLabel}
          style={[styles.row, selected && styles.rowSelected]}
        >
          {body}
        </View>
      )}
      {onRepeat ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t("mobile.workouts.repeat")}
          style={({ pressed }) => [
            styles.repeatButton,
            pressed && styles.repeatButtonPressed,
          ]}
          onPress={() => onRepeat(item.id)}
          disabled={repeating || opening}
        >
          {repeating ? (
            <ActivityIndicator size="small" color={theme.palette.brand500} />
          ) : (
            <Icon
              name="refresh"
              size={theme.typography.sizes.md}
              color={theme.palette.ink2}
            />
          )}
        </Pressable>
      ) : null}
    </View>
  );
}

export function MonthSectionHeader({
  section,
  onToggle,
  styles,
  locale,
}: {
  section: WorkoutMonthSection;
  onToggle: (key: string) => void;
  styles: WorkoutsStyles;
  locale: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={monthLabel(section.key, locale)}
      accessibilityState={{ expanded: !section.collapsed }}
      onPress={() => onToggle(section.key)}
      style={({ pressed }) => [
        styles.sectionHeader,
        pressed && styles.sectionHeaderPressed,
      ]}
    >
      <Text style={styles.sectionTitle}>{monthLabel(section.key, locale)}</Text>
      <View style={styles.sectionRight}>
        <Text style={styles.sectionCount}>{section.total}</Text>
        <Icon
          name={section.collapsed ? "chevron-right" : "chevron-down"}
          size={styles.sectionChevron.fontSize ?? 16}
          color={styles.sectionChevron.color}
        />
      </View>
    </Pressable>
  );
}
